import React from "react";
import { View, KeyboardAvoidingView, ScrollView } from "react-native";

import { isEqual } from "lodash";
import moment from "moment";

import { ReflectionProvider } from "../ReflectionContext";
import Utils, {
  escapeRegexp,
  getStorageItem,
  setStorageItem,
} from "../../../../helpers/utils";

import AppMessages from "../../../../config/AppMessages";
import EditElementsView from "../EditElementsView";
import appStyles from "../../../appStyles";
import ReflectionHelper from "../ReflectionHelper";

import AppTheme from "../../AppTheme";
import SettingsHelper from "../../more/SettingsHelper";
import ComparisonCard from "../../home/cards/reflection/ComparisonCard";
import ComparisonFabButton from "./ComparisonFabButton";
import Search from "../../common/search-bar/Search";
import ComparisonHeader from "./ComparisonHeader";
import JournalDialog from "../../common/JournalDialog";
import ViewModes from "../header/viewModes";

const SyncInProgressMessage = AppMessages.SyncInProgress;
const DATE_FORMAT = "YYYY-MM-DD";

export default class ComparisonPage extends React.PureComponent {
  constructor(props) {
    super(props);
    this.controller = props.controller;
    this.useElements = props.useElements;

    this.settingsStorageKey = SettingsHelper.getPreferencesStorageKey(
      this.controller,
    );

    const viewMode = ViewModes.chartView;

    this.state = {
      pageTitle: "Time Use Comparisons",
      selectedJournalGoal: null,
      showEditView: false,
      viewMode,
      comparisons: [],
      selectedDate: null,
      timeUnit: null
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.initData();
        },
      );
    } else {
      this.initData();
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  getArgs() {
    if (this.controller.hasNavigation()) {
      return this.props.route.params?.args;
    } else {
      return this.controller.state.screenArgs?.args?.args;
    }
  }

  initData = async () => {
    const preferences = (await getStorageItem(this.settingsStorageKey)) || {};
    const timeUnit = preferences.timeUnit || "week";

    // After creating new SET go to edit sets view
    const { show_edit_sets_view } = this.getArgs() || {};

    if (show_edit_sets_view) {
      this.setState((state) => ({ ...state, showEditView: true }));

      const { selectedDate } = this.state;
      this.getCompareDataFromApi({ selectedDate, timeUnit });
    } else {
      // Monday logic for prev or current week
      const selectedDate = ReflectionHelper.getReflectionSelectedDate({
        viewMode: ViewModes.insights,
        userSelectedDate: null,
        timeUnit,
      }).format(DATE_FORMAT);

      this.setState((state) => ({ ...state, selectedDate, timeUnit }));

      // Display old data if available to avoid empty screen
      const oldComparisonData = this.controller.state?.comparisons;
      if (oldComparisonData) {
        this.setComparisonModel({
          comparisons: oldComparisonData,
          selectedDate,
          timeUnit,
        });
      }

      // Check if newer version of data is available
      this.getCompareDataFromApi({ selectedDate, timeUnit });
    }
  };

  getCompareDataFromApi = ({
    selectedDate,
    timeUnit = "week",
    forceUpdate = false,
  }) => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
      mariMessage: SyncInProgressMessage,
    }));

    const queryDate = moment(selectedDate).add(1, "day").format(DATE_FORMAT);

    const oldComparisonData = this.controller.state?.comparisons;
    const params = selectedDate
      ? {
          reflectionDate: queryDate,
          time_unit: timeUnit,
          useElements: !!this.useElements,
          fullComparison: true,
        }
      : null;

    this.controller.getApiData("comparisons", params, (comparisons) => {
      if (!forceUpdate && isEqual(oldComparisonData, comparisons)) {
        this.setState((state) => ({
          ...state,
          isLoading: false,
          mariMessage: "",
          term: "",
          selectedDate,
          timeUnit,
        }));
        return;
      }

      this.setComparisonModel({
        comparisons,
        selectedDate,
        timeUnit,
        isFromApi: true,
      });
    });
  };

  setComparisonModel = ({
    selectedDate,
    comparisons = [],
    timeUnit = "week",
    isFromApi = false,
  }) => {
    this.setState(
      (state) => ({
        ...state,
        isLoading: false,
        mariMessage: isFromApi ? "" : SyncInProgressMessage,
        term: "",
        comparisons: [...comparisons],
        allComparisons: [...comparisons],
        selectedDate,
        timeUnit,
      }),
      () => {
        if (isFromApi && this.state.showEditView) this.showEditSetsView();
      },
    );
  };

  filterByDate = async (selectedDate, timeUnit) => {
    const preferences = (await getStorageItem(this.settingsStorageKey)) || {};
    setStorageItem(this.settingsStorageKey, {
      ...preferences,
      timeUnit,
    });

    this.getCompareDataFromApi({ selectedDate, timeUnit });
  };

  refreshData = () => {
    const { selectedDate, timeUnit } = this.state;

    this.setState(
      (state) => ({
        ...state,
        showEditView: false,
        selectedSetIds: [],
        showEditSets: false,
      }),
      () => this.getCompareDataFromApi({ selectedDate, timeUnit }),
    );
  };

  goBack = () => {
    const { showEditSets } = this.state;

    if (showEditSets) {
      this.setState((state) => ({ ...state, showEditSets: false }));
    } else {
      this.controller.goBack();
    }
  };

  // ------------------------------------- SEARCH -------------------------------------
  filterResults = ({ term }) => {
    const { allComparisons } = this.state;

    const pattern = Utils.getSearchPattern(term);
    const filtered = term
      ? allComparisons.filter((c) => {
          return c.element?.name && c.element.name.match(pattern);
        })
      : allComparisons;

    this.setState((state) => ({
      ...state,
      isLoading: false,
      comparisons: [...filtered],
    }));
  };

  // ------------------------------------- FAB -------------------------------------
  showEditSetsView = () => {
    const { comparisons = [] } = this.state;

    const selectedSetIds = comparisons.map((g) => g.id_element);
    this.setState((state) => ({
      ...state,
      selectedSetIds,
      showEditSets: true,
    }));
  };

  toggleJournalDialog = (elem) => {
    this.setState((state) => ({
      ...state,
      selectedGoalElem: elem,
    }));
  };

  onJurnalActionDone = () => {
    const { selectedDate, timeUnit } = this.state;

    this.setState((state) => ({
      ...state,
      selectedGoalElem: null,
    }));

    // Refresh
    this.getCompareDataFromApi({ selectedDate, timeUnit, forceUpdate: true });
  };

  render() {
    const {
      comparisons,
      isLoading,
      pageTitle,
      selectedDate,
      selectedSetIds,
      viewMode,
      showEditSets,
      timeUnit,
      selectedGoalElem,
    } = this.state;

    return (
      <ReflectionProvider
        value={{
          controller: this.controller,
          viewMode,
          selectedDate,
          timeUnit,
          isComparisonPage: true,
          filterByDate: this.filterByDate,
          goBack: this.goBack,
        }}
      >
        {/* Journal dialog for Top Comparison Card */}
        {selectedGoalElem ? (
          <JournalDialog
            type={"reflection"}
            item={selectedGoalElem}
            controller={this.controller}
            close={() => this.toggleJournalDialog(null)}
            onJournalComplete={this.onJurnalActionDone}
          />
        ) : null}

        {showEditSets ? (
          <EditElementsView
            controller={this.controller}
            selectedSetIds={selectedSetIds}
            goBack={this.refreshData}
            isFromComparisonPage={true}
          />
        ) : (
          <View
            style={[
              appStyles.container,
              { backgroundColor: AppTheme.containerBg },
            ]}
          >
            <KeyboardAvoidingView style={appStyles.kbViewNoFooter}>
              <ComparisonHeader
                controller={this.controller}
                isLoading={isLoading}
                title={pageTitle}
              />

              <ScrollView
                contentContainerStyle={appStyles.scrollViewContent}
                style={[
                  appStyles.scrollView,
                  {
                    paddingHorizontal: 0,
                    backgroundColor: AppTheme.containerBg,
                    marginTop: 0,
                  },
                ]}
              >
                {comparisons.map((data, index) => (
                  <ComparisonCard
                    key={data.card_id}
                    controller={this.controller}
                    data={{ ...data, selectedDate, time_unit: timeUnit }}
                    showMore={false}
                    toggleGoalJournalDialog={this.toggleJournalDialog}
                    refreshHandler={() => this.refreshData()}
                  />
                ))}
              </ScrollView>

              <View style={appStyles.footerOptions}>
                <ComparisonFabButton showEditSetsView={this.showEditSetsView} />
              </View>

              <Search
                style={{ bottom: 5 }}
                placeholder="Search elements"
                controller={this.controller}
                filterResults={this.filterResults}
                inputStyle={{ backgroundColor: "#FFF" }}
              />
            </KeyboardAvoidingView>
          </View>
        )}
      </ReflectionProvider>
    );
  }
}
